import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Paystack redirects here after payment. URL carries `?reference=…&trxref=…`.
 * We POST the reference to the backend `/paystack/verify/{reference}` which
 * queries Paystack for the true transaction status. Backend is idempotent —
 * even if the webhook already credited the wallet before this page loaded,
 * verifying again is safe and simply returns `credited_now: false`.
 *
 * The URL is scrubbed of the reference immediately so a browser Back doesn't
 * re-trigger verification and a shared link never leaks reference codes.
 */
export default function PaystackCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const ran = useRef(false);
  const [state, setState] = useState({ status: "working", detail: "", points: 0 });

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(loc.search);
    const reference = params.get("reference") || params.get("trxref");
    // Scrub reference from browser history before any await. Preserve whichever
    // callback path the user actually landed on (`/wallet/topup/callback` or
    // the shorter `/callback` alias used by some Paystack dashboards).
    window.history.replaceState({}, "", loc.pathname);

    if (!reference) {
      setState({ status: "error", detail: "No payment reference in the callback URL." });
      return;
    }

    (async () => {
      try {
        const { data } = await api.get(`/paystack/verify/${reference}`);
        if (data.status === "success") {
          setState({
            status: "ok",
            detail: data.credited_now
              ? `+${data.points} Credits landed in your wallet.`
              : `Payment already verified (+${data.points} Credits).`,
            points: data.points,
          });
          setTimeout(() => nav("/wallet", { replace: true }), 1500);
        } else {
          setState({
            status: "error",
            detail: `Paystack reported: ${data.status}. Your card was not charged.`,
          });
        }
      } catch (e) {
        setState({
          status: "error",
          detail: e?.response?.data?.detail || e?.message || "Verification failed",
        });
      }
    })();
  }, [loc.search, nav]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6"
         data-testid="paystack-callback-page">
      <Card className="bg-white/5 border-white/10 max-w-md w-full">
        <CardContent className="p-6 space-y-4 text-center">
          <h1 className="font-heading text-xl font-black">
            {state.status === "working" && "Verifying payment…"}
            {state.status === "ok" && "Credits added"}
            {state.status === "error" && "Payment could not be verified"}
          </h1>
          {state.status === "working" && (
            <div className="flex justify-center" data-testid="paystack-callback-spinner">
              <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
            </div>
          )}
          {state.status === "ok" && state.points > 0 && (
            <div className="text-5xl font-heading font-black text-yellow-300" data-testid="paystack-credited">
              +{state.points}
            </div>
          )}
          <p className="text-sm text-white/70" data-testid="paystack-callback-detail">{state.detail}</p>
          {state.status === "error" && (
            <Button onClick={() => nav("/wallet/buy", { replace: true })}
                    data-testid="paystack-back">
              Back to Buy Credits
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
